import { httpService } from "../../base/services/httpService.service";

// Every server call the Help Center makes.

/** The first page of the Tickets page's list, and how many tickets there are. */
export const fetchRecentTickets = async (count = 3) => {
   const params = new URLSearchParams({ page: 1, pageSize: count });
   const response = await httpService.get(`/Tickets/getAllTickets?${params.toString()}`);
   return {
      tickets: response.data?.ticketResponses ?? [],
      total: response.data?.total ?? 0
   };
};

// Enough earlier turns to follow up on; the backend refuses more.
export const MAX_ASK_HISTORY = 10;

// A turn longer than this is refused, and the path of a page this long is not
// a page anyone is on. Both are cut here rather than checked, so a long answer
// still carries the thread instead of costing the user their question.
export const MAX_ASK_CONTENT = 4000;
export const MAX_ASK_PAGE = 300;

/** Shown when a failed ask has no wording of its own worth reading. */
export const ASK_ERROR_FALLBACK = "We had a problem answering that, please try again.";

/**
 * The body of POST /HelpCenter/ask. The backend answers from its own copy of
 * the knowledge base, so only the question travels - see BACKEND_HANDOFF.md,
 * which also says who to tell when the help content changes. Roles are spelled
 * the way /DataCollection/aiChat spells them.
 */
export const buildAskRequest = ({ question, history = [], page }) => ({
   question: question.trim(),
   history: history.slice(-MAX_ASK_HISTORY).map(({ role, content }) => ({
      role: /^user$/i.test(role) ? "User" : "Assistant",
      content: (content ?? "").slice(0, MAX_ASK_CONTENT)
   })),
   page: page?.slice(0, MAX_ASK_PAGE)
});

/**
 * Ask AI: `{ answer, sources }`, where `answer` is Markdown whose
 * [text](#chapter-id) links open the User Guide article with that id and
 * `sources` are the ids of the chapters it used (up to three, none when the
 * knowledge base didn't cover the question).
 */
export const askHelpAssistant = async ({ question, history, page }) => {
   const request = buildAskRequest({ question, history, page });
   const response = await httpService.post("/HelpCenter/ask", request);
   return response.data;
};

// A rejected call carries the envelope either on its own (a 200 that isn't a
// success) or inside the axios error (any other status).
const errorEnvelope = (error) => error?.response?.data ?? error ?? {};

// Codes whose text is written for the user: what was wrong with the question,
// or how long the rate limit lasts. Every other failure is about the model, so
// its wording would tell the user nothing. The code decides, never the wording.
const SPEAKS_FOR_ITSELF = ["INVALID_REQUEST", "RATE_LIMITED"];

/** What the error bubble says after a failed ask. */
export const askErrorMessage = (error) => {
   const body = errorEnvelope(error);
   if (!SPEAKS_FOR_ITSELF.includes(body.code)) return ASK_ERROR_FALLBACK;
   return body.message || body.errors?.[0] || ASK_ERROR_FALLBACK;
};

/** "Was this article helpful?" `{ articleId, helpful, comment, page }`. */
export const sendArticleFeedback = async (feedback) => {
   await httpService.post("/HelpCenter/articleFeedback", feedback);
};
